/*
 * NOTE: this files is excluded from ts-unused-exports because its exports are used by Vue SFC files
 */

import { type AxiosResponse } from 'axios/index';

const types = ['options', 'hierarchy'] as const;
const renderTypes = ['Options', 'OptionsSingleMode'] as const;

export function handlePushState (currentSearchTerm: string): void {
    let newUrl = window.location.href.split('?')[0];
    if (currentSearchTerm !== '*') {
        newUrl += '?tx_solr[q]=' + currentSearchTerm;
    }
    history.pushState({ q: currentSearchTerm }, '', newUrl);
}

export function handlePopState (callbackFunction: (searchTerm: string) => void): void {
    window.addEventListener('popstate', (event: PopStateEvent) => {
        if (event.state !== null && typeof event.state === 'object' && event.state.q !== null && typeof event.state.q === 'string') { // eslint-disable-line @typescript-eslint/no-unsafe-member-access
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            const q: string = event.state.q as string;
            callbackFunction(q);
        }
    });
}

export function getFacetsByFieldName (allFacets: SolrFacet[], fieldNames: string[]): SolrFacet[] {
    const facets: SolrFacet[] = [];
    allFacets.forEach((facet) => {
        if (!facet.available) {
            console.debug(`Facet '${facet.field}' is unavailable.`);
            return;
        }

        const userFieldIndex = fieldNames.indexOf(facet.name);
        if (userFieldIndex > -1) {
            if (isFacetType(facet.type) || isFacetRenderType(facet.renderType)) {
                facets[userFieldIndex] = facet; // order facets according to userFields array
            } else {
                if (!isFacetType(facet.type)) {
                    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                    console.trace(`Facet ${facet.field} has unsupported type '${facet.type}'.`);
                }
                if (!isFacetRenderType(facet.renderType)) {
                    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                    console.trace(`Facet ${facet.field} has unsupported renderType '${facet.renderType}'.`);
                }
            }
        }
    });

    return facets;
}

export type FacetType = typeof types[number];
export type FacetRenderType = typeof renderTypes[number];

export function isFacetType (x: string): x is FacetType {
    return types.includes(x as FacetType);
}

export function isFacetRenderType (x: string): x is FacetRenderType {
    return renderTypes.includes(x as FacetRenderType);
}

export interface SolrFacet {
    field: string
    name: string
    label: string
    type: FacetType
    renderType: FacetRenderType
    selected: boolean
    available: boolean
    options: SolrFacetOption[]
}

export interface SolrFacetOption {
    label: string
    value: string
    count: number
    selected: boolean
    children: SolrFacetOption[]
}

export interface SolrArguments {
    filter: string[]
    page: string
    perPage: string
    q: string
    sort: string
}

export interface SolrDocument {
    [key: string]: string | number | boolean | Array<string | number> | undefined

    content: string
    contentHtml: string
    highlightedContent?: string
    id: string
    uid: number
    title: string
    url: string
    category: string
    type: string
    mainCategoryTitle: string
}

export interface SolrFundingProgramsDocument extends SolrDocument {
}

export interface SolrJobDocument extends SolrDocument {
    jobTypeId: number
    jobType: string
    jobExpertise: string
    jobWorkplace: string
    jobPlaceOfWork: string
    jobClosingDate: string
    jobFirstPublished: string
    content: string
    teaser: string
    jobLanguageCode: string
}

export interface SolrNewsDocument extends SolrDocument {
    newsStarttime: string
    mainCategoryTitle: string
    abstract: string
    newsScilogTeaser: string
    processedTeaserImage: string
    processedTeaserImageAlternative: string
    processedCategoryImage: string
    processedCategoryImageAlternative: string
}

export interface SolrEventDocument extends SolrNewsDocument {
    newsEventLocation: string
    newsEventCountry: string
    newsEventType: number
    newsEventIsIntern: string
}

export interface SolrPublicationDocument extends SolrDocument {
    publicationAuthors: string
    publicationDate: string
    publicationDoiText: string
    publication: string
    publicationFileExtension: string
    publicationFileSize: string
    publicationLanguageLabel: string
    publicationLanguage: number
    subCategoryTitle: string
}

export interface SolrEmployeeDocument extends SolrDocument {
    employeeFirstName: string
    employeeLastName: string
    // see \Plan2net\FwfSolr\Slots\SearchResultsForEmployees::resultsActionSlot for details how this is constructed
    employeeEmail?: [string, string, string, string]
    employeeCollectiveEmail?: [string, string, string, string]
    employeeMobile?: string
    employeePhone?: string
    employeeFunctionsWithAreas?: [string]
}

export interface SolrResponseData {
    arguments: SolrArguments
    currentPage: number
    documents: SolrDocument[]
    facets: SolrFacet[]
    lastPage: number
    pluginNamespace: string
    resultCount: number
    spellCheckingSuggestions: [string]
}

export interface ParsedSolrResponseData {
    documents: SolrDocument[]
    facets: SolrFacet[]
    currentPageNumber: number
    totalNumberOfPages: number
}

export class RequestUrl {
    public readonly url: URL;
    public readonly pluginNamespace: string;

    constructor (baseUrl: URL | RequestUrl, pluginNamespace = '', perPage = 10) {
        if (baseUrl instanceof RequestUrl) {
            this.url = new URL(baseUrl.url);
            this.pluginNamespace = baseUrl.pluginNamespace;
        } else {
            this.url = new URL(baseUrl);
            this.url.searchParams.set(`${pluginNamespace}[perPage]`, perPage.toString());
            this.pluginNamespace = pluginNamespace;
        }
    }

    static buildFromArguments (endpoint: string, pluginNamespace: string, solrArguments: SolrArguments): RequestUrl {
        const url = new URL(endpoint);
        let perPage = 0;
        for (const [key, value] of Object.entries(solrArguments)) {
            if (Array.isArray(value)) {
                value.forEach((value) => {
                    url.searchParams.append(`${pluginNamespace}[${key}][]`, value as string);
                });
            } else {
                if (key === 'perPage') {
                    perPage = parseInt(value as string);
                }
                url.searchParams.set(`${pluginNamespace}[${key}]`, value as string);
            }
        }

        if (perPage > 0) {
            return new this(url, pluginNamespace, perPage);
        }
        return new this(url, pluginNamespace);
    }

    public addFilterParameter (filter: string): void {
        this.url.searchParams.append(`${this.pluginNamespace}[filter][]`, filter);
    }

    public removeFilterParameter (filter: string): void {
        const parameterName = `${this.pluginNamespace}[filter][]`;
        const oldParameters = this.url.searchParams.getAll(`${this.pluginNamespace}[filter][]`);
        const newParameters = oldParameters.filter((parameter) => parameter !== filter);
        this.url.searchParams.delete(parameterName);
        newParameters.forEach((value) => { this.url.searchParams.append(parameterName, value); });
    }

    public removeFilter (filterName: string): void {
        const parameterName = `${this.pluginNamespace}[filter][]`;
        const oldParameters = this.url.searchParams.getAll(`${this.pluginNamespace}[filter][]`);
        const newParameters = oldParameters.filter((parameter) => {
            const [name] = parameter.split(':');
            return name !== filterName;
        });
        this.url.searchParams.delete(parameterName);
        newParameters.forEach((value) => { this.url.searchParams.append(parameterName, value); });
    }

    public setPage (page: number): void {
        this.url.searchParams.set(`${this.pluginNamespace}[page]`, page.toString());
    }

    public setQuery (query: string): void {
        this.url.searchParams.set(`${this.pluginNamespace}[q]`, query);
    }

    public setSorting (sorting: string): void {
        this.url.searchParams.set(`${this.pluginNamespace}[sort]`, sorting);
    }

    public toString (): string {
        return this.url.toString();
    }
}

export function parseSolrResponse (response: AxiosResponse<SolrResponseData>, facetFields: string[]): ParsedSolrResponseData {
    return {
        facets: getFacetsByFieldName(response.data.facets, facetFields),
        documents: response.data.documents,
        currentPageNumber: response.data.currentPage,
        totalNumberOfPages: response.data.lastPage
    };
}
